import { getAccountStorage } from '@weavix/mobx';
import { NativeStorage } from '@weavix/sync';
import { coerceToDate } from '@weavix/utils/src/coerce-to-date';
import { WebNativeStorage } from 'weavix-shared/services/sync/web-native-storage';

interface ChannelArchiveItem {
    archived?: Date;
}

export class ChannelMessageArchiver {
    constructor(
        private readonly storage: NativeStorage = new WebNativeStorage('channel-archive'),
    ) { }

    async archiveChannelMessages(channelId: string, archived: Date, messageTopic: string): Promise<Set<string>> {
        const prevArchived = (await this.getArchived(channelId) ?? new Date(0));
        if (!archived || (archived.getTime() <= prevArchived.getTime())) return new Set();
        await this.setArchived(channelId, archived);

        console.log(`Archiving messages for ${channelId} older than ${archived.toISOString()}`);
        const store = getAccountStorage<any>({ name: 'channel-messages', topics: messageTopic, readonly: false }).Instance();
        return await store.index.removeMany(({
            isMatch: (_id, index) => {
                const [chId, dateStr] = index?.split('|') ?? [];
                return chId === channelId
                    && coerceToDate(dateStr) < archived;
            },
        }));
    }

    private async getArchived(channelId: string): Promise<Date | null> {
        return (await this.getItem(channelId)).archived;
    }

    private async setArchived(channelId: string, date: Date) {
        await this.updateItem(channelId, {
            archived: date,
        });
    }

    private async updateItem(channelId: string, partialUpdate: Partial<ChannelArchiveItem>) {
        await this.storage.setMap(channelId, {
            ...(await this.getItem(channelId)),
            ...partialUpdate,
        });
    }

    private async getItem(channelId: string): Promise<ChannelArchiveItem> {
        const item = await this.storage.getMap(channelId);
        return {
            archived: (item?.archived as Date) ?? null,
        };
    }
}
